import type { CustomerAnalyticsMoodysScenarioDetailsDto } from '@zg-rentals/ts-customer-analytics-client';
import { type CustomerAnalyticsMoodysReportDto } from '@zg-rentals/ts-customer-analytics-client';
import { Image, Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import { MarketTrendsColumn, MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import type { Scenario } from './ScenarioLineChart';
import { getRowStyle } from './pdfImageUtils';

const currentYear = new Date().getFullYear();

export const ScenarioTrendsPage = ({
  report,
  scenarioChartSrc,
  scenario,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  scenarioChartSrc: string;
  scenario: Scenario;
}) => {
  const yearFilter = (item: CustomerAnalyticsMoodysScenarioDetailsDto) =>
    currentYear <= item.year && item.year <= currentYear + 10;

  const submarketData = report.scenarioTrendsReport.submarket;
  const marketData = report.scenarioTrendsReport.market;

  const hasSubmarketData = submarketData.length > 0;

  const getItemData = (item: CustomerAnalyticsMoodysScenarioDetailsDto | undefined) =>
    item &&
    (scenario === 'RENT'
      ? { value: item.askingRentChange, s1: item.askingRentChangeS1, s2: item.askingRentChangeS2 }
      : scenario === 'INVENTORY'
        ? { value: item.inventoryChange, s1: item.inventoryChangeS1, s2: item.inventoryChangeS2 }
        : { value: item.vacancyPercent, s1: item.vacancyPercentS1, s2: item.vacancyPercentS2 });

  const data = marketData
    .filter(yearFilter)
    .sort((a, b) => a.year - b.year)
    .map((item) => {
      const itemData = getItemData(item);
      if (!itemData) return;

      const subItemData = getItemData(submarketData?.find((item2) => item2.year === item.year));
      const nationData = getItemData(report.scenarioTrendsReport.national?.find((item2) => item2.year === item.year));
      return {
        name: item.year,
        market: itemData.value,
        submarket: subItemData?.value,
        s1: subItemData?.s1,
        s2: subItemData?.s2,
        nation: nationData?.value,
      };
    });

  const totalCols = hasSubmarketData ? 6 : 3;

  const colWidth = 100 / totalCols;
  const submarketColWidth = colWidth * 3;

  const heading =
    'Scenarios: ' + (scenario === 'RENT' ? 'Asking Rent Per Unit' : scenario === 'INVENTORY' ? 'Inventory' : 'Vacancy');

  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        <MarketTrendsTextHeader headerText={heading} paddingBottom={20} />
        <View
          style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', marginTop: 20, marginBottom: 40 }}
        >
          <View>
            <Image src={scenarioChartSrc} />
          </View>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <MarketTrendsColumn width={colWidth} />
            {hasSubmarketData && (
              <MarketTrendsColumn width={submarketColWidth} textAlign="center">
                <Text>{report.submarketName}</Text>
              </MarketTrendsColumn>
            )}
            <MarketTrendsColumn width={colWidth} textAlign="center">
              <Text>{report.marketName}</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>{'United States'}</Text>
            </MarketTrendsColumn>
          </View>
          <View style={styles.tableHeader}>
            <MarketTrendsColumn width={colWidth} textAlign="center">
              <Text>Year</Text>
            </MarketTrendsColumn>
            {hasSubmarketData && (
              <>
                <MarketTrendsColumn width={colWidth}>
                  <Text>% Change</Text>
                </MarketTrendsColumn>

                <MarketTrendsColumn width={colWidth}>
                  <Text>S1 - % Change</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={colWidth}>
                  <Text>S2 - % Change</Text>
                </MarketTrendsColumn>
              </>
            )}
            <MarketTrendsColumn width={colWidth}>
              <Text>% Change</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={colWidth}>
              <Text>% Change</Text>
            </MarketTrendsColumn>
          </View>
          {data.map(
            (item, i) =>
              item && (
                <View style={{ display: 'flex', ...getRowStyle(i) }} key={item.name + item.market}>
                  <MarketTrendsColumn width={colWidth}>
                    <Text> {item.name}</Text>
                  </MarketTrendsColumn>
                  {item.submarket !== undefined && (
                    <MarketTrendsColumn width={colWidth}>
                      <Text> {(item.submarket * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  )}
                  {hasSubmarketData && item.s1 !== undefined && (
                    <MarketTrendsColumn width={colWidth}>
                      <Text> {(item.s1 * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  )}
                  {hasSubmarketData && item.s2 !== undefined && (
                    <MarketTrendsColumn width={colWidth}>
                      <Text> {(item.s2 * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  )}

                  {
                    <MarketTrendsColumn width={colWidth}>
                      <Text>{(item.market * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  }
                  {item.nation !== undefined && (
                    <MarketTrendsColumn width={colWidth}>
                      <Text>{(item.nation * 100).toFixed(1)}%</Text>
                    </MarketTrendsColumn>
                  )}
                </View>
              ),
          )}
        </View>
        <View style={styles.textContainer}>
          <View style={styles.textHeadingParagraph}>
            <Text style={styles.footerText}>
              <Text style={styles.footerHeading}>Baseline Scenario:</Text> This is the most likely outcome based on
              current conditions and economic forecasts. It includes multiple possible outcomes, both better and worse,
              and is updated monthly over a 30-year horizon. The baseline forecast is at the 50th percentile of possible
              outcomes. Key inputs include monitoring new construction projects, historical property and market
              performance, and Moody&apos;s baseline macroeconomic forecast.
            </Text>
          </View>
          {Boolean(report.submarketName) && (
            <>
              <View style={styles.textHeadingParagraph}>
                <Text style={styles.footerText}>
                  <Text style={styles.footerHeading}>S1 (Moderate Downside Scenario):</Text> This scenario represents a
                  more pessimistic outlook, positioned at the 90th percentile of downside outcomes. It uses the same
                  inputs as the baseline but incorporates Moody&apos;s &ldquo;deep second recession&rdquo; forecast.
                </Text>
              </View>
              <View style={styles.textHeadingParagraph}>
                <Text style={styles.footerText}>
                  <Text style={styles.footerHeading}>S2 (Severe Downside Scenario):</Text> This scenario is even more
                  pessimistic, at the 96th percentile of downside outcomes. It also uses the same inputs but applies
                  Moody&apos;s &ldquo;protracted slump&rdquo; forecast.
                </Text>
              </View>
            </>
          )}
        </View>
      </View>
    </MarketTrendsPageWrapper>
  );
};
